import * as React from 'react'

import { useFormContext, type RegisterOptions } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { cn } from '@/lib/utils'

import { controlVariants, type ControlSize } from './control-styles'
import { useMergedRef } from './lib/merge-refs'
import { useControlName } from './lib/use-control-name'
import { useLegacyFieldAria } from './lib/use-legacy-field-aria'

/**
 * Base dos campos mascarados.
 *
 * A máscara é aplicada pelo `ref`, então este controle mescla **três** refs: o
 * do `use-mask-input`, o do `register` e o que vem do `Field.Control`.
 *
 * Quando `mask` é um array, o `use-mask-input` escolhe sozinho a máscara pelo
 * comprimento do que foi digitado. É o que resolve documento (CPF/CNPJ) e
 * telefone (fixo/celular) sem observar o valor nem recriar a máscara na mão.
 */

export type MaskedFieldProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'size' | 'name'
> & {
  size?: ControlSize
  mask: string | string[]
  /**
   * Entrega ao formulário o valor **sem** a máscara — só os dígitos. A entrada
   * segue mostrando o texto formatado; o que muda é o que trafega.
   *
   * Existe porque máscara é formatação e payload é contrato: `phone_prefix` e
   * `phone` da API são dígitos puros, e a feature que monta esse payload não
   * deveria ter de desfazer uma formatação que a camada acabou de aplicar.
   * Aditivo por decisão — sem a prop, o valor gravado continua o formatado.
   */
  unmasked?: boolean
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  /** Regras do react-hook-form. Prefira validar por schema; isto existe para
   *  paridade com os campos legados durante a migração. */
  rules?: RegisterOptions
}

export const MaskedField = React.forwardRef<HTMLInputElement, MaskedFieldProps>(
  (
    {
      className,
      size,
      mask,
      unmasked = false,
      inputMode = 'numeric',
      name: nameProp,
      rules,
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    const name = useControlName(nameProp)
    const legacyAria = useLegacyFieldAria(name)
    const { register } = useFormContext()

    // `setValueAs` é o ponto do react-hook-form onde o valor lido do campo é
    // normalizado antes de entrar no formulário — e é o único que cobre os dois
    // caminhos de leitura. Transformar apenas no `onChange` deixa o valor certo
    // enquanto se digita e o errado depois: no blur o RHF relê o nó, que está
    // mascarado, e sobrescreve o que havia sido normalizado. O sintoma seria o
    // pior possível — payload correto em teste de digitação, formatado no envio
    // real, porque quem preenche sai do campo antes de enviar.
    const registerRules: RegisterOptions | undefined = unmasked
      ? { ...rules, setValueAs: value => String(value ?? '').replace(/\D/g, '') }
      : rules

    const { ref: registerRef, onChange: registerChange, onBlur: registerBlur, ...field } =
      register(name, registerRules)

    // O handler do consumidor é composto com o do register, nunca o substitui:
    // espalhar `props` por cima de `field` faria um `onChange` de fora derrubar
    // o registro do formulário, e o campo pararia de gravar valor em silêncio.
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      void registerChange(event)
      onChange?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      void registerBlur(event)
      onBlur?.(event)
    }

    const cleanupRef = React.useRef<(() => void) | null>(null)

    const maskRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        // `withMask` devolve a função de limpeza. Descartá-la deixa o
        // observador da máscara preso ao nó anterior a cada troca de máscara ou
        // desmontagem — vazamento silencioso, que só aparece como campo lento
        // depois de muitas remontagens.
        cleanupRef.current?.()
        cleanupRef.current = null

        if (node) {
          const cleanup = withMask(mask)(node)
          cleanupRef.current = typeof cleanup === 'function' ? cleanup : null
        }
      },
      // `mask` pode ser array literal; a serialização evita reaplicar a máscara
      // a cada render por identidade de referência nova.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [JSON.stringify(mask)]
    )

    const mergedRef = useMergedRef(maskRef, registerRef, ref)

    return (
      <input
        type="text"
        inputMode={inputMode}
        className={cn(controlVariants({ size }), className)}
        {...legacyAria}
        {...field}
        {...props}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={mergedRef}
      />
    )
  }
)
MaskedField.displayName = 'MaskedField'
